import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../store/authStore';
import { Header } from '../components/Header';
import { SkipLink } from '../components/SkipLink';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Search, 
  Trash2, 
  Eye, 
  Calendar,
  Clock,
  MessageSquare,
  AlertCircle,
  Download
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ResponseViewer = () => {
  const { qrId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [qrCode, setQrCode] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [filteredSubmissions, setFilteredSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [viewingDetail, setViewingDetail] = useState(false);
  const [detailData, setDetailData] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [qrResponse, submissionsResponse] = await Promise.all([
        axios.get(`${API}/qr-codes/${qrId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/qr-codes/${qrId}/submissions`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setQrCode(qrResponse.data);
      setSubmissions(submissionsResponse.data);
      setFilteredSubmissions(submissionsResponse.data);
    } catch (error) {
      toast.error('Failed to load responses');
      navigate('/qr-codes');
    } finally {
      setLoading(false);
    }
  }, [qrId, token, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredSubmissions(submissions);
    } else {
      const searchLower = searchTerm.toLowerCase();
      setFilteredSubmissions(submissions.filter(sub => {
        const responsesString = JSON.stringify(sub.responses).toLowerCase();
        const dateString = new Date(sub.completed_at).toLocaleDateString().toLowerCase();
        return responsesString.includes(searchLower) || dateString.includes(searchLower);
      }));
    }
  }, [searchTerm, submissions]);


  const viewSubmissionDetail = async (submissionId) => {
    try {
      const response = await axios.get(`${API}/qr-codes/${qrId}/submissions/${submissionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDetailData(response.data);
      setSelectedSubmission(submissionId);
      setViewingDetail(true);
    } catch (error) {
      toast.error('Failed to load submission details');
    }
  };

  const deleteSubmission = async (submissionId) => {
    if (!window.confirm('Are you sure you want to delete this submission? This action cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(`${API}/qr-codes/${qrId}/submissions/${submissionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Submission deleted');
      setSubmissions(prev => prev.filter(s => s.id !== submissionId));
      if (viewingDetail && selectedSubmission === submissionId) {
        setViewingDetail(false);
        setSelectedSubmission(null);
      }
    } catch (error) {
      toast.error('Failed to delete submission');
    }
  };

  const exportToJSON = () => {
    const dataStr = JSON.stringify(filteredSubmissions, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = window.URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${qrCode?.title.replace(/\s+/g, '-')}-responses.json`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    toast.success('Responses exported as JSON');
  };

  const formatTime = (seconds) => {
    if (!seconds) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <SkipLink />
        <Header />
        <main id="main-content" className="flex items-center justify-center min-h-screen">
          <div className="text-center" role="status" aria-live="polite">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1d4ed8] mx-auto" aria-hidden="true"></div>
            <p className="mt-4 text-[#475569]">Loading responses...</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <SkipLink />
      <Header />
      
      <main id="main-content" className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <Button
              variant="ghost"
              onClick={() => navigate(`/qr-codes/${qrId}/analytics`)}
              className="mb-4 rounded-full"
              aria-label="Back to analytics"
            >
              <ArrowLeft className="h-4 w-4 mr-2" aria-hidden="true" />
              Back to Analytics
            </Button>
            
            <div className="flex justify-between items-start">
              <div>
                <h1 className="text-3xl sm:text-4xl font-black mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Form Responses
                </h1>
                <p className="text-[#475569]">{qrCode?.title}</p>
              </div>
              
              <Button
                onClick={exportToJSON}
                disabled={filteredSubmissions.length === 0}
                className="rounded-full bg-[#1d4ed8] hover:bg-[#1e40af]"
                aria-label="Export responses as JSON"
              >
                <Download className="h-4 w-4 mr-2" aria-hidden="true" />
                Export JSON
              </Button>
            </div>
          </div>

          {/* Search */}
          <div className="mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-[#475569]" aria-hidden="true" />
              <input
                type="text"
                placeholder="Search responses..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-[#E2E8F0] rounded-lg focus:ring-2 focus:ring-[#1d4ed8] focus:border-transparent"
                aria-label="Search responses"
              />
            </div>
            {searchTerm && (
              <p className="mt-2 text-sm text-[#475569]" role="status" aria-live="polite">
                Found {filteredSubmissions.length} of {submissions.length} responses
              </p>
            )}
          </div>

          {filteredSubmissions.length === 0 ? (
            <Card className="p-12 text-center border border-[#E2E8F0]">
              <AlertCircle className="h-12 w-12 text-[#475569] mx-auto mb-4" aria-hidden="true" />
              <p className="text-[#475569]">
                {searchTerm ? 'No responses match your search' : 'No submissions yet. Share your QR code to start collecting responses!'}
              </p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* List View */}
              <div className="space-y-4">
                <h2 className="text-xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  All Submissions ({filteredSubmissions.length})
                </h2>
                
                {filteredSubmissions.map((submission, index) => (
                  <motion.div
                    key={submission.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Card 
                      className={`p-4 border transition-all cursor-pointer ${
                        selectedSubmission === submission.id 
                          ? 'border-[#1d4ed8] bg-blue-50' 
                          : 'border-[#E2E8F0] hover:border-[#1d4ed8]'
                      }`}
                      onClick={() => viewSubmissionDetail(submission.id)}
                      role="button"
                      tabIndex={0}
                      aria-label={`View submission ${filteredSubmissions.length - index}`}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          viewSubmissionDetail(submission.id);
                        }
                      }}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <span className="font-medium">Submission #{filteredSubmissions.length - index}</span>
                            <span className="text-xs text-[#475569]">
                              {Object.keys(submission.responses).length} responses
                            </span>
                          </div>
                          
                          <div className="flex items-center text-sm text-[#475569] space-x-4">
                            <div className="flex items-center">
                              <Calendar className="h-3 w-3 mr-1" aria-hidden="true" />
                              {new Date(submission.completed_at).toLocaleDateString()}
                            </div>
                            <div className="flex items-center">
                              <Clock className="h-3 w-3 mr-1" aria-hidden="true" />
                              {formatTime(submission.completion_time_seconds)}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              viewSubmissionDetail(submission.id);
                            }}
                            className="rounded-full"
                            aria-label="View details"
                          >
                            <Eye className="h-4 w-4" aria-hidden="true" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteSubmission(submission.id);
                            }}
                            className="rounded-full text-red-600 hover:text-red-700 hover:bg-red-50"
                            aria-label="Delete submission"
                          >
                            <Trash2 className="h-4 w-4" aria-hidden="true" />
                          </Button>
                        </div>
                      </div>
                      
                      {/* Preview of first response */}
                      {Object.keys(submission.responses).length > 0 && (
                        <div className="mt-3 pt-3 border-t border-[#E2E8F0]">
                          <p className="text-sm text-[#475569] truncate">
                            {Object.entries(submission.responses)[0][0]}: {String(Object.entries(submission.responses)[0][1])}
                          </p>
                        </div>
                      )}
                    </Card>
                  </motion.div>
                ))}
              </div>

              {/* Detail View */}
              <div className="lg:sticky lg:top-24 h-fit">
                <AnimatePresence mode="wait">
                  {viewingDetail && detailData ? (
                    <motion.div
                      key="detail"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                    >
                      <Card className="p-6 border border-[#E2E8F0]">
                        <div className="flex justify-between items-start mb-6">
                          <h2 className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            Submission Details
                          </h2>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => deleteSubmission(selectedSubmission)}
                            className="rounded-full text-red-600 hover:text-red-700 hover:bg-red-50"
                            aria-label="Delete this submission"
                          >
                            <Trash2 className="h-4 w-4 mr-1" aria-hidden="true" />
                            Delete
                          </Button>
                        </div>

                        {/* Metadata */}
                        <div className="mb-6 space-y-3">
                          <div className="flex items-center text-sm">
                            <Calendar className="h-4 w-4 mr-2 text-[#475569]" aria-hidden="true" />
                            <span className="text-[#475569]">Completed:</span>
                            <span className="ml-2 font-medium">
                              {new Date(detailData.session.completed_at).toLocaleString()}
                            </span>
                          </div>
                          
                          <div className="flex items-center text-sm">
                            <Clock className="h-4 w-4 mr-2 text-[#475569]" aria-hidden="true" />
                            <span className="text-[#475569]">Duration:</span>
                            <span className="ml-2 font-medium">
                              {(() => {
                                const created = new Date(detailData.session.created_at);
                                const completed = new Date(detailData.session.completed_at);
                                return formatTime(Math.floor((completed - created) / 1000));
                              })()}
                            </span>
                          </div>
                          
                          <div className="flex items-center text-sm">
                            <MessageSquare className="h-4 w-4 mr-2 text-[#475569]" aria-hidden="true" />
                            <span className="text-[#475569]">Messages:</span>
                            <span className="ml-2 font-medium">
                              {detailData.messages.length}
                            </span>
                          </div>
                        </div>

                        {/* Responses */}
                        <div className="mb-6">
                          <h3 className="font-bold mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            Responses
                          </h3>
                          
                          {Object.keys(detailData.session.responses).length === 0 ? (
                            <p className="text-sm text-[#475569]">No responses recorded</p>
                          ) : (
                            <div className="space-y-3">
                              {Object.entries(detailData.session.responses).map(([question, answer], idx) => (
                                <div key={idx} className="p-3 bg-[#F8FAFC] rounded-lg border border-[#E2E8F0]">
                                  <p className="text-sm font-medium text-[#475569] mb-1">{question}</p>
                                  <p className="text-sm">{String(answer)}</p>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Conversation History */}
                        <div>
                          <h3 className="font-bold mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            Conversation History
                          </h3>
                          <div className="space-y-2 max-h-96 overflow-y-auto">
                            {detailData.messages.map((message, idx) => (
                              <div
                                key={idx}
                                className={`p-3 rounded-lg text-sm ${
                                  message.role === 'user'
                                    ? 'bg-blue-50 border border-blue-200 ml-4'
                                    : 'bg-[#F8FAFC] border border-[#E2E8F0] mr-4'
                                }`}
                              >
                                <p className="text-xs text-[#475569] mb-1 font-medium">
                                  {message.role === 'user' ? 'User' : 'Assistant'}
                                </p>
                                <p>{message.content}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      </Card>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="placeholder"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                    >
                      <Card className="p-12 text-center border border-[#E2E8F0]">
                        <Eye className="h-12 w-12 text-[#475569] mx-auto mb-4" aria-hidden="true" />
                        <p className="text-[#475569]">Select a submission to view details</p>
                      </Card>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};
